import { createSlice } from '@reduxjs/toolkit'
import { keyBy } from '../../../../helpers/common'
import { register } from '../../../../helpers/reducerRegistry'
import { MODEL_ID, VIEW_BY } from '../constant'
import { getTopHighestIndustry, getTopHighestPortfolio } from './thunk'

const initialState = {
  viewBy: VIEW_BY.CREDIT_RISK_SCORE,

  highestRiskPortfolioIds: [],
  highestRiskPortfolio: {},

  highestRiskIndustryIds: [],
  highestRiskIndustry: {},

  highestGrowthPortfolioIds: [],
  highestGrowthPortfolio: {},

  highestGrowthIndustryIds: [],
  highestGrowthIndustry: {},

  loading: {
    highestRiskPortfolio: true,
    highestRiskIndustry: true,
    highestGrowthPortfolio: true,
    highestGrowthIndustry: true,
  },
}

const slice = createSlice({
  name: 'portfolio/summary/topHighest',
  initialState,
  reducers: {
    resetStorePortfolioTopHighest: () => initialState,
    changeViewBy: (state, action) => {
      state.viewBy = action.payload
    },
  },
  extraReducers: (builder) => {
    //getTopHighestPortfolio
    builder.addCase(getTopHighestPortfolio.pending, (state, action) => {
      const isRisk = action.meta.arg.modelId === MODEL_ID.CORPORATE_RISK
      if (isRisk) {
        state.loading.highestRiskPortfolio = true
      } else {
        state.loading.highestGrowthPortfolio = true
      }
    })
    builder.addCase(getTopHighestPortfolio.fulfilled, (state, action) => {
      const isRisk = action.meta.arg.modelId === MODEL_ID.CORPORATE_RISK
      if (isRisk) {
        const data = (action.payload || []).map((item) => ({
          ...item,
          id: item.portfolioId,
          name: item.portfolioName,
          numberCompanies: item.numberCompanies,
          highestCompanies: item.highestGrowthCompanies, //highestRisk
          newHighestCompanies: item.newHighestGrowthCompanies, //highestRisk
        }))
        state.highestRiskPortfolioIds = data.map((item) => item.id)
        state.highestRiskPortfolio = keyBy(data, 'id')
        state.loading.highestRiskPortfolio = false
      } else {
        const data = (action.payload || []).map((item) => ({
          ...item,
          id: item.portfolioId,
          name: item.portfolioName,
          numberCompanies: item.numberCompanies,
          highestCompanies: item.highestGrowthCompanies,
          newHighestCompanies: item.newHighestGrowthCompanies,
        }))
        state.highestGrowthPortfolioIds = data.map((item) => item.id)
        state.highestGrowthPortfolio = keyBy(data, 'id')
        state.loading.highestGrowthPortfolio = false
      }
    })
    builder.addCase(getTopHighestPortfolio.rejected, (state, action) => {
      const isRisk = action.meta.arg.modelId === MODEL_ID.CORPORATE_RISK
      if (isRisk) {
        state.loading.highestRiskPortfolio = action.payload.loading
      } else {
        state.loading.highestGrowthPortfolio = action.payload.loading
      }
    })
    //getTopHighestIndustry
    builder.addCase(getTopHighestIndustry.pending, (state, action) => {
      const isRisk = action.meta.arg.modelId === MODEL_ID.CORPORATE_RISK
      if (isRisk) {
        state.loading.highestRiskIndustry = true
      } else {
        state.loading.highestGrowthIndustry = true
      }
    })
    builder.addCase(getTopHighestIndustry.fulfilled, (state, action) => {
      const isRisk = action.meta.arg.modelId === MODEL_ID.CORPORATE_RISK
      if (isRisk) {
        const data = (action.payload || []).map((item) => ({
          ...item,
          id: item.vsicId,
          name: item.vsicName,
          numberCompanies: item.numberCompanies,
          highestCompanies: item.highestGrowthCompanies, //highestRisk
          newHighestCompanies: item.newHighestGrowthCompanies, //highestRisk
        }))
        state.highestRiskIndustryIds = data.map((item) => item.id)
        state.highestRiskIndustry = keyBy(data, 'id')
        state.loading.highestRiskIndustry = false
      } else {
        const data = (action.payload || []).map((item) => ({
          ...item,
          id: item.vsicId,
          name: item.vsicName,
          numberCompanies: item.numberCompanies,
          highestCompanies: item.highestGrowthCompanies,
          newHighestCompanies: item.newHighestGrowthCompanies,
        }))
        state.highestGrowthIndustryIds = data.map((item) => item.id)
        state.highestGrowthIndustry = keyBy(data, 'id')
        state.loading.highestGrowthIndustry = false
      }
    })
    builder.addCase(getTopHighestIndustry.rejected, (state, action) => {
      const isRisk = action.meta.arg.modelId === MODEL_ID.CORPORATE_RISK
      if (isRisk) {
        state.loading.highestRiskIndustry = action.payload.loading
      } else {
        state.loading.highestGrowthIndustry = action.payload.loading
      }
    })
  },
})

export const selectViewBy = (state) => state[slice.name].viewBy

export const selectHighestRiskPortfolioIds = (state) =>
  state[slice.name].highestRiskPortfolioIds
export const selectHighestRiskPortfolioById = (id, attr) => (state) => {
  const dataById = state[slice.name].highestRiskPortfolio
  if (!id) return dataById
  if (!attr) return dataById[id]
  return dataById[id][attr]
}

export const selectHighestRiskIndustryIds = (state) =>
  state[slice.name].highestRiskIndustryIds
export const selectHighestRiskIndustryById = (id, attr) => (state) => {
  const dataById = state[slice.name].highestRiskIndustry
  if (!id) return dataById
  if (!attr) return dataById[id]
  return dataById[id][attr]
}

export const selectHighestGrowthPortfolioIds = (state) =>
  state[slice.name].highestGrowthPortfolioIds
export const selectHighestGrowthPortfolioById = (id, attr) => (state) => {
  const dataById = state[slice.name].highestGrowthPortfolio
  if (!id) return dataById
  if (!attr) return dataById[id]
  return dataById[id][attr]
}

export const selectHighestGrowthIndustryIds = (state) =>
  state[slice.name].highestGrowthIndustryIds
export const selectHighestGrowthIndustryById = (id, attr) => (state) => {
  const dataById = state[slice.name].highestGrowthIndustry
  if (!id) return dataById
  if (!attr) return dataById[id]
  return dataById[id][attr]
}

export const selectLoading = (type) => (state) => {
  const loadingObj = state[slice.name].loading
  if (!type) return Object.values(loadingObj).some(Boolean)
  return loadingObj[type]
}

export const { resetStorePortfolioTopHighest, changeViewBy } = slice.actions

register(slice.name, slice.reducer)
